import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';
import ArticleSEO from './components/ArticleSEO';

function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return views.toString();
}

export default function VideoDetail() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '0', 10);

  const video = useMemo(() => videos.find((v) => v.id === videoId), [videoId]);
  const relatedVideos = useMemo(
    () => videos.filter((v) => v.id !== videoId).slice(0, 4),
    [videoId]
  );

  const categoryInfo = useMemo(
    () => categories.find((c) => c.slug === video?.category),
    [video]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-video-off-line text-foreground-400 text-3xl"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">视频未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的视频不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ArticleSEO
        title={video.title}
        description={video.description}
        author={video.author}
        thumbnailUrl={video.thumbnail}
        categoryName={categoryInfo?.name || '全部'}
        categorySlug={video.category}
        articleId={video.id}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: categoryInfo?.name || '全部', path: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          <article className="mt-4">
            <h1 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4 leading-snug">
              {video.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <img
                  src={video.authorAvatar}
                  alt={video.author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="font-medium text-foreground-800 whitespace-nowrap">{video.author}</span>
              </div>
              <span className="whitespace-nowrap">
                <i className="ri-eye-line mr-1"></i>{formatViews(video.views)} 次观看
              </span>
              <span className="whitespace-nowrap">
                <i className="ri-time-line mr-1"></i>{video.duration}
              </span>
              {categoryInfo && (
                <Link
                  to={`/category/${video.category}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium cursor-pointer hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {categoryInfo.name}
                </Link>
              )}
            </div>

            <div className="relative aspect-video rounded-xl overflow-hidden mb-6 bg-background-900">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer group">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center group-hover:bg-primary-500 transition-all group-hover:scale-110 duration-300">
                  <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1"></i>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-8">
              <h3 className="font-heading text-base text-foreground-900 mb-3">视频简介</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {video.description}
              </p>
            </div>
          </article>

          <section className="mt-10 mb-8">
            <h3 className="font-heading text-xl text-foreground-900 mb-5">
              相关推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedVideos.map((v) => (
                <Link
                  key={v.id}
                  to={`/article/${v.id}`}
                  className="group cursor-pointer"
                >
                  <article className="rounded-xl overflow-hidden bg-background-100 hover:bg-background-100 transition-all duration-300">
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                        {v.duration}
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors">
                        {v.title}
                      </h4>
                      <div className="flex items-center gap-2">
                        <img
                          src={v.authorAvatar}
                          alt={v.author}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{v.author}</span>
                        <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>{formatViews(v.views)}
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}